/**
 * Local Partners Web Push — hotel_wl pattern (VAPID + service worker).
 * Alerts when a resident places a Neighbour order, including locked phone.
 */
import api from '../api';

const SW_PATH = '/sw.js';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

export function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isStandalonePwa() {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator;
  return (
    nav.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches
  );
}

export function isPushApiSupported() {
  if (typeof window === 'undefined') return false;
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function registerServiceWorker() {
  if (!isPushApiSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_PATH, { scope: '/' });
    await navigator.serviceWorker.ready;
    return reg;
  } catch {
    return null;
  }
}

export async function fetchLpVapidPublicKey() {
  try {
    const res = await api.get('/api/towers/shop/push/vapid-public-key');
    const data = res.data || {};
    return data.enabled && data.publicKey ? data.publicKey : null;
  } catch {
    return null;
  }
}

/**
 * Request permission, subscribe PushManager, register endpoint with API.
 * Returns true when subscribed.
 */
export async function subscribeLpPush() {
  if (!isPushApiSupported()) return false;
  if (isIosDevice() && !isStandalonePwa()) return false;

  const vapid = await fetchLpVapidPublicKey();
  if (!vapid) return false;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const reg = await registerServiceWorker();
  if (!reg) return false;

  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });
  }

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false;

  try {
    await api.post('/api/towers/shop/push/subscribe', {
      endpoint: json.endpoint,
      keys: {
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      },
      expirationTime: json.expirationTime ?? null,
    });
    try {
      localStorage.setItem('lp_notif_enabled_v1', '1');
      localStorage.setItem('lp_notif_nudge_dismissed_v1', '1');
    } catch {
      /* ignore */
    }
    return true;
  } catch {
    return false;
  }
}

/** Re-sync existing browser subscription to API (e.g. after login). */
export async function syncLpPushSubscription() {
  if (!isPushApiSupported()) return false;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
  try {
    const vapid = await fetchLpVapidPublicKey();
    if (!vapid) return false;
    const reg = await registerServiceWorker();
    if (!reg) return false;
    let subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid),
      });
    }
    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false;
    await api.post('/api/towers/shop/push/subscribe', {
      endpoint: json.endpoint,
      keys: {
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      },
      expirationTime: json.expirationTime ?? null,
    });
    return true;
  } catch {
    return false;
  }
}
